<template>
  <div>
    <div
      v-show="selected.length"
      class="pull-right">
      Selected ({{ selected.length }})
      <b-dropdown
        variant="light"
        right
        text="Actions">
        <b-dropdown-item
          v-for="(action, index) in bulkActions()"
          :key="index"
          @click.prevent="action.handler(selected)">
          {{ action.label }}
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <base-search-form
      v-if="config.searchFields"
      :search-fields="config.searchFields"
      class="mb-4"
      @submit="config.onSearch"/>
    <b-table
      ref="table"
      :fields="fields"
      :items="itemsProvider"
      v-model="items"
      :sort-by="config.sortBy"
      :sort-desc="config.sortDesc"
      :filter="filter"
      :per-page="config.perPage"
      :current-page="page"
      show-empty>
      <template
        slot="table-colgroup">
        <slot name="table-colgroup"/>
      </template>
      <template
        slot-scope="row"
        slot="HEAD__checkbox">
        <input
          :checked="isAllSelected"
          type="checkbox"
          @click.stop
          @change="onSelectAll">
      </template>
      <template
        v-for="(field, key) in resource.fields"
        slot-scope="row"
        :slot="`HEAD_${key}`">
        <div
          :key="key">
          {{ field.label || key }}
        </div>
      </template>
      <template
        slot-scope="{ item }"
        slot="_checkbox">
        <input
          v-model="selected"
          :value="item.id"
          type="checkbox">
      </template>
      <template
        v-for="field in resource.fields"
        slot-scope="{ item }"
        :slot="field.key">
        <component
          v-if="field.component"
          :key="field.key"
          :is="field.component"
          :attribute="field.key"
          :config="field.config"
          :item="item"/>
        <div
          v-if="!field.component"
          :key="field.key">
          <slot
            :name="field.key"
            :field="field"
            :item="item">
            {{ $refs.table.getFormattedValue(item, field) }}
          </slot>
        </div>
      </template>
      <template
        slot-scope="{ item }"
        slot="_actions">
        <template v-for="(action, key) in rowActions(item)">
          <template v-if="action.type === 'link' || action.label === 'Edit'">
            <a 
              :key="key" 
              :variant="action.variant"
              :class="action.class || 'btn ml-1 btn-'+action.variant+' btn-secondary btn-sm'"
              :href="action.link || '#'">
              {{ action.label }}
            </a>
          </template>
          <template v-else>
            <b-button
              :key="key"
              :variant="action.variant"
              size="sm"
              class="ml-1"
              @click="action.handler">
              {{ action.label }}
            </b-button>
          </template>
          <slot 
            :item="item" 
            name="actions"/>
        </template>
      </template>
    </b-table>
    <b-row
      no-gutters>
      <b-pagination
        v-if="config.pagination"
        v-model="page"
        :limit="10"
        :total-rows="total"
        :per-page="config.perPage"/>
      <b-col>
        <h4 class="text-right">
          <b-badge
            size="lg"
            class="mt-1 ml-4">Total count: {{ total }}</b-badge>
        </h4>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import defaults from 'lodash-es/defaults';
import pick from 'lodash-es/pick';
import get from 'lodash-es/get';
import map from 'lodash-es/map';
import intersection from 'lodash-es/intersection';
import SlotMixin from '../mixins/slot';
import BaseSearchForm from './BaseSearchForm.vue';
import TreeMixin from '../mixins/TreeMixin'

export default {
  name: 'PagesBaseTable',

  components: {
    BaseSearchForm,
  },

  mixins: [SlotMixin, TreeMixin],

  props: {
    resource: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      config: {},
      proxy: null,
      items: [],
      selected: [],
      total: 0,
      page: 1,
      filter: null,
      params: {},
      editing: null,
    };
  },

  computed: {

    fields() {
      const fields = [];

      fields.push({
        key: '_checkbox',
        label: '',
        sortable: false,
      });

      this.config.fields.forEach((field) => {
        fields.push(pick(field, [
          'key',
          'label',
          'sortable',
          'class',
          'formatter',
        ]));
      });

      fields.push({
        key: '_actions',
        label: '',
        tdAttr: {
          nowrap: 'nowrap',
          width: '1%',
        },
      });

      return fields;
    },

    isAllSelected() {
      if (!this.items.length) {
        return false;
      }

      const selected = intersection(this.selected, map(this.items, 'id'));

      return (selected.length === this.items.length);
    },
  },

  created() {
    defaults(this.config, this.resource, {
      proxy: () => {
        console.error('Table resource should provide proxy() method.');
      },

      beforeItemsRequest: () => {
      },

      pagination: true,

      perPage: 15,

      fields: [],

      searchFields: [
        {
          attribute: 'query',
          component: 'text-field',
          type: 'search',
          placeholder: 'Search ...',
          value: '',
        },
      ],

      onSearch: (params) => {
        this.params = params;
        this.reset();
      },

      rowActions: () => [
        'edit',
        'delete',
      ],

      bulkActions: () => ['delete'],

      onEditItem: (item) => {        console.log(item);
        this.$router.push({
          name: `${this.resource.base}.edit`,
          params: {
            id: item.id,
          },
        });
      },

      onDeleteItem: (item) => {
        this.$alert.question('Proceed with deleting?', 'Please, confirm deleting this record.', {
          showCancelButton: true,
        })
          .then((result) => {
            if (!result.value) {
              return;
            }
            this.config.proxy().destroy(item.id)
              .then(() => {
                this.refresh();
              });
          });
      },

      onBulkDelete: (selected) => {
        this.$alert.question('Proceed with deleting?', 'Please, confirm deleting selected records.', {
          showCancelButton: true,
        })
          .then((result) => {
            if (!result.value) {
              return;
            }

            this.config.proxy()
              .bulkDestroy(selected)
              .then(() => {
                this.selected = [];
                this.refresh();
              });
          });
      },
    });

    this.initProxy();
  },

  methods: {

    initProxy() {
      this.proxy = this.config.proxy();
    },

    refresh() {
      this.$refs.table.refresh();
    },

    reset() {
      this.initProxy();
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.$nextTick(() => {
          this.refresh();
        });
      }
    },

    itemsProvider(ctx) {
      this.initProxy();
      this.proxy.setParameters(this.params);
      this.proxy.setParameter('per_page', ctx.perPage);
      this.proxy.setParameter('page', ctx.currentPage);

      if (ctx.sortBy) {
        this.proxy.setParameter('sort', {[ctx.sortBy]: ctx.sortDesc ? 'desc' : 'asc'});
      } else {
        this.proxy.removeParameter('sort');
      }

      this.config.beforeItemsRequest(this.proxy, ctx);

      return this.proxy
        .all()
        .then((result) => {
          this.total = get(result, 'meta.total', result.data.length);

          if (!this.resource.fields) {
            this.resource.fields = result.fields;
          }

          return result.data;
        })
        .catch(() => []);
    },

    rowActions(item) {
      const actions = [];

      this.config.rowActions(item).forEach((action) => {
        if (action === 'edit') {
          actions.push({
            label: 'Edit',
            link: this.$router.resolve({
              name: `${this.resource.base}.edit`,
              params: {
                id: item.id
              }
            }).href,
            handler: () => {
              this.config.onEditItem(item);
            },
          });
        } else if (action === 'delete') {
          actions.push({
            label: 'Delete',
            variant: 'danger',
            handler: () => {
              this.config.onDeleteItem(item);
            },
          });
        } else {
          actions.push(action);
        }
      });

      return actions;
    },

    onSelectItem(item) {
      const {id} = item;
      if (this.selected.indexOf(id) === -1) {
        this.selected.push(id);
      } else {
        this.selected.splice(this.selected.indexOf(id), 1);
      }
    },

    bulkActions() {
      const actions = [];

      this.config.bulkActions().forEach((action) => {
        if (action === 'delete') {
          actions.push({
            label: 'Delete selected',
            variant: 'danger',
            handler: this.config.onBulkDelete,
          });
        } else {
          actions.push(action);
        }
      });

      return actions;
    },

    onSelectAll() {
      if (this.isAllSelected) {
        this.items.forEach((item) => {
          const {id} = item;
          if (this.selected.indexOf(id) !== -1) {
            this.selected.splice(this.selected.indexOf(id), 1);
          }
        });
        return;
      }

      this.items.forEach((item) => {
        const {id} = item;
        if (this.selected.indexOf(id) === -1) {
          this.selected.push(id);
        }
      });
    },

  },

};
</script>
